/* eslint-disable no-prototype-builtins */
import type { NextPage } from 'next'
import Head from 'next/head'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import type { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { format, formatISO, startOfToday, addDays } from 'date-fns'
import { isEmpty } from 'lodash-es'
import { QueryClient, dehydrate } from 'react-query'

// Libs
import { fetchCategoryMenu } from '@/api/fetchCategoryMenu'
import { fetchCategoryFilter } from '@/api/fetchCategoryFilter'
import { fetchCategoryList } from '@/api/fetchCategoryList'
import removeSearchParams from '@/methods/url/removeSearchParams'
import { utmSearchParams } from '@/methods/url/getSearchParams'
import { getSchemaOrg } from '@/methods/handleSchemaOrg'
import { fetchRecentLiveStreamingProductList } from '@/api/fetchRecentLiveStreamingProductList'
import { fetchEventName } from '@/api/fetchEventName'
import { fetchPopularProducts } from '@/api/fetchPopularProducts'

// Components
import MallComponent from '@/components/pages/Mall'

// Constants
import { envConfigs } from '@/constants/config'
import { MALL_DEFAULT_PAGE_SIZE, DEFAULT_MALL_SORT, DEFAULT_MALL_ORDER } from '@/constants/mall'
import { DEFAULT_LOCALE } from '@/constants/defaultLocale'
import { ROUTE_PATH } from '@/constants/routePath'
import { QUERY_KEY } from '@/constants/queryKey'

const DAYS_OF_THREE_MONTHS = 90

const Mall: NextPage = ({ pageProps: data }: any) => {
  const { mallDataSSR } = data

  const { t } = useTranslation()

  const router = useRouter()

  const currentLocale = router.locale === 'default' ? DEFAULT_LOCALE : router.locale

  // 這裡的 canonical 需要 theme_id 或 category_id，但要把 utmSearchParams 移除
  const canonicalUrl = `${envConfigs.domainUrl}/${currentLocale}${router.asPath}`

  const newCanonicalUrl = removeSearchParams({
    url: canonicalUrl,
    removeKeyList: utmSearchParams,
  })

  const schema = getSchemaOrg({
    type: 'WebSite',
    name: t('Meta_Title_Mall', { brandName: 'TutorABC Shop' }),
    description: t('Meta_Description_Live', { brandName: 'TutorABC Shop' }),
    url: newCanonicalUrl as string,
    image: '/images/ogimage-main.png',
    breadCrumbList: [
      {
        name: t('Meta_Title_Live', { brandName: 'TutorABC Shop' }),
        position: 1,
        url: `${envConfigs.domainUrl}/${currentLocale}${ROUTE_PATH.LIVE}`,
      },
    ],
  })

  return (
    <>
      <Head>
        <title>{t('Meta_Title_Mall', { brandName: 'TutorABC Shop' })}</title>
        <link rel='shortcut icon' href='/favicon.ico' />
        <link rel='canonical' href={newCanonicalUrl} />
        <meta name='title' content={t('Meta_Title_Mall', { brandName: 'TutorABC Shop' })} />
        <meta name='description' key='meta-description' content={t('Meta_Description_Live', { brandName: 'TutorABC Shop' })} />
        <meta property='og:title' key='title' content={t('Meta_Title_Mall', { brandName: 'TutorABC Shop' })} />
        <meta property='og:description' key='description' content={t('Meta_Description_Live', { brandName: 'TutorABC Shop' })} />
        <meta property='og:type' key='type' content='website' />
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      </Head>
      <MallComponent mallDataSSR={mallDataSSR} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ res, query, locale }) => {
  const isTestMode = query.hasOwnProperty('testMode') && query.testMode === '1' ? '1' : '0'
  const categoryId = Number(query?.category_id) || Number(query?.category) || -1
  const order = Array.isArray(query?.order) ? query.order[0] : query?.order || DEFAULT_MALL_ORDER
  const sortBy = Array.isArray(query?.sortBy) ? query.sortBy[0] : query?.sortBy || DEFAULT_MALL_SORT
  const hasCategoryId = !!categoryId
  const hasEventId = query.hasOwnProperty('event_id')
  let categoryMenu = null
  let categoryList = null
  let categoryListData = null
  let eventName = null
  let popularProductListData = null
  let fetchCategoryMenuData = null
  let fetchCategoryFilterData = null
  let fetchCategoryListData = null
  let recentLiveStreamingProductData = null

  const queryClient = new QueryClient()

  if (hasCategoryId) {
    try {
      ;[fetchCategoryMenuData, fetchCategoryFilterData, fetchCategoryListData] = await Promise.all([
        fetchCategoryMenu({ ssrLocale: locale, params: { test_mode: isTestMode === '1' ? '1' : undefined } }),
        fetchCategoryFilter({ ssrLocale: locale, params: {} }),
        fetchCategoryList(categoryId, { pageSize: MALL_DEFAULT_PAGE_SIZE, pageNo: 1, order, sortBy }),
      ])
    } catch (error) {
      console.log('fetchCategoryMenu fetchCategoryFilter fetchCategoryList', error)
    }

    categoryMenu = fetchCategoryMenuData || []
    categoryList = fetchCategoryFilterData || []
    categoryListData = fetchCategoryListData || { productsInfo: [] }
  } else {
    try {
      ;[fetchCategoryMenuData, fetchCategoryFilterData] = await Promise.all([
        fetchCategoryMenu({ ssrLocale: locale, params: {} }),
        fetchCategoryFilter({ ssrLocale: locale, params: {} }),
      ])
    } catch (error) {
      console.log('fetchCategoryMenu fetchCategoryFilter', error)
    }

    categoryMenu = fetchCategoryMenuData || []
    categoryList = fetchCategoryFilterData || []
  }

  // PM 要求關閉 http://tp-jira.tutorabc.com/browse/SHOP-1306
  // try {
  //   eventName = await fetchEventName({ ssrLocale: locale, params: { identifier: 'shop_event_product' } })
  // } catch (error) {
  //   console.log('fetchEventName Error', error)
  // }

  if (hasEventId) {
    try {
      popularProductListData = await fetchPopularProducts({ ssrLocale: locale, params: { event: 'shop_event_product' } })
    } catch (error) {
      popularProductListData = { productsInfo: [] }
    }
  }

  try {
    recentLiveStreamingProductData = await fetchRecentLiveStreamingProductList({
      categoryId: categoryId,
      startTime: formatISO(new Date(format(startOfToday(), 'yyyy/MM/dd 00:00:00'))),
      endTime: formatISO(new Date(format(addDays(startOfToday(), DAYS_OF_THREE_MONTHS), 'yyyy/MM/dd 00:00:00'))),
      pageNo: 1,
      pageSize: 20,
      group: false,
    })
  } catch (error) {
    console.log('recentLiveStreamingProductData', error)
  }

  res.setHeader('Cache-Control', 'public, s-maxage=7200, stale-while-revalidate=7260')

  const infiniteQueryData = {
    pages: [fetchCategoryListData],
    pageParams: [1],
  }

  queryClient.setQueryData([QUERY_KEY.FETCH_CATEGORY_LIST, { categoryId, order, sortBy }], infiniteQueryData)

  const dehydratedState = dehydrate(queryClient)

  return {
    props: {
      mallDataSSR: {
        categoryMenuSSR: categoryMenu || [],
        categoryListSSR: categoryList || [],
        eventNameSSR: null, // eventName fetch is disabled per SHOP-1306
        popularProductListSSR:
          !isEmpty(popularProductListData?.productsInfo) && popularProductListData.productsInfo.length > 0
            ? popularProductListData
            : {
                productsInfo: [],
              },
        categoryListDataSSR: categoryListData || { productsInfo: [] },
        recentLiveStreamingProductDataSSR: recentLiveStreamingProductData || {},
      },
      dehydratedState: dehydratedState,

      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  }
}

export default Mall
